import { useOutletContext } from 'react-router';

import type { Analysis } from '@/entities/analysis';

/**
 * Props shared by every analysis section. In the live app the section renders
 * routed under the `AnalysisResult` outlet and reads `analysis` from context; on
 * the PDF print page it is rendered standalone with `analysis` passed as a prop.
 */
export interface AnalysisSectionProps {
  analysis?: Analysis;
  /**
   * Set on the standalone PDF print page. Suppresses the per-section download
   * button so it never prints into the exported PDF.
   */
  exporting?: boolean;
}

/**
 * Resolve the section's `analysis`, whether it renders inside the app (via the
 * `AnalysisResult` outlet) or standalone on the `/download/iq-analysis/:id` print
 * page (via an explicit prop). The prop wins; when absent we read the outlet
 * context, which the router populates for the live routed sections.
 */
export const useAnalysisFromContext = (analysis?: Analysis): Analysis => {
  const outlet = useOutletContext<{ analysis: Analysis } | null>();
  const resolved = analysis ?? outlet?.analysis;
  if (!resolved) {
    throw new Error('Analysis section rendered without an `analysis` prop or outlet context.');
  }
  return resolved;
};
